import throttle from 'lodash.throttle'
import log from './utils/logger'
import { loadScript } from './utils/utils'
import { bannerTypeArray, GAME_TARGET_ID, GOOGLE_PUBLISHER_TAG } from './constants'
import { startAdsAuction, refreshBidAds } from './prebid/auction-handler'
import {
  addBannerDiv,
  getGameMinWidth,
  getGameMinHeight,
  hideAd,
  showAd,
  setGameIframeSize,
  getGameIframeSize,
  bannersControl,
  minWindowWidthHorizontalAd,
} from './html-manager'
import { actionTypes, trackIga, getBannerRefreshTrackedParameters } from './tracking'
import { BannersConfigWithDefault } from './configs/game-configs'

let gameWidthWithAd = '100vw'
let gameHeightWithAd = '100vh'
let gameMarginTop: number
let widthObserver: MutationObserver
let heightObserver: MutationObserver
let minGameWidth: number
let minGameHeight: number
let adPlaceholderWidth: number
let adPlaceholderHeight: number
let globalRefreshTime: number
const adSlot: googletag.Slot[] = []

const gameStyleWidth = () => {
  return bannersControl.vertical.isShown ? gameWidthWithAd : '100vw'
}

const gameStyleHeight = () => {
  return bannersControl.horizontal.isShown ? gameHeightWithAd : '100vh'
}

const gameStyleMarginTop = () => {
  return bannersControl.horizontal.isShown ? `${gameMarginTop}px` : '0px'
}

let refreshHorizontalTimeoutRef: NodeJS.Timeout
let refreshVerticalTimeoutRef: NodeJS.Timeout
const slotOnloadHandler = (refreshTime: number) => (event: googletag.events.SlotOnloadEvent) => {
  const slotId = event.slot.getSlotElementId()
  log(`Creative iframe load event has fired. slotId: ${slotId}`)
  if (slotId.includes('horizontal')) {
    refreshHorizontalSlot(refreshTime)
  } else if (slotId.includes('vertical')) {
    refreshVerticalSlot(refreshTime)
  } else {
    log('No slot to refresh')
  }
}

export const refreshHorizontalSlot = (refreshTime: number, immediately = false) => {
  refreshHorizontalTimeoutRef = setTimeout(() => {
    refreshBidAds([adSlot[0]])
    clearTimeout(refreshHorizontalTimeoutRef)
  }, refreshTime * 1000)

  if (immediately) {
    refreshBidAds([adSlot[0]])
  }
}

export const refreshVerticalSlot = (refreshTime: number, immediately = false) => {
  refreshVerticalTimeoutRef = setTimeout(() => {
    refreshBidAds([adSlot[1]])
    clearTimeout(refreshVerticalTimeoutRef)
  }, refreshTime * 1000)

  if (immediately) {
    refreshBidAds([adSlot[1]])
  }
}

const initGPTLogic = (refreshTime: number, adSlotSettings: BannersConfigWithDefault['adSlotSettings']) => {
  log('Creating ad slots')
  window.googletag = window.googletag || { cmd: [] }
  window.googletag.cmd.push(() => {
    try {
      for (const adSlotSetting of adSlotSettings) {
        const sizeMapping = window.googletag.sizeMapping()
        if (adSlotSetting.sizeMap) {
          for (const [viewportSize, slotSize] of adSlotSetting.sizeMap) {
            sizeMapping.addSize(viewportSize, slotSize)
          }
          // log(`sizeMapping: `, sizeMapping.build())
        }

        const auxSlot = window.googletag.defineSlot(
          adSlotSetting.unitPath,
          adSlotSetting.defaultAdSize,
          adSlotSetting.htmlElements.adContainer.id
        )

        if (auxSlot) {
          adSlot.push(
            auxSlot.addService(window.googletag.pubads()).defineSizeMapping(sizeMapping.build()) // the sizeMapping must be build from defined sizes!!!
          )
        } else {
          log(`Error while creating ad slot: ${adSlotSetting.unitPath}`)
        }
      }

      window.googletag.pubads().addEventListener('slotOnload', slotOnloadHandler(refreshTime))

      window.googletag.pubads().addEventListener('slotVisibilityChanged', (event) => {
        const isPassiveTab = event.inViewPercentage === 0

        if (isPassiveTab) {
          onPassiveTabHandler()
        } else {
          onActiveTabHandler()
        }
      })

      window.googletag.pubads().addEventListener('slotRenderEnded', (event) => {
        const slot = event.slot
        const slotId = slot.getSlotElementId()
        const bannerType = getBannerType(slotId)

        trackIga(actionTypes.BANNER_REFRESHED, bannerType, getBannerRefreshTrackedParameters(event))

        // Log details of the rendered ad.
        log('Slot %s finished rendering. %O', slotId, {
          'Advertiser ID': event.advertiserId,
          'Campaign ID': event.campaignId,
          'Creative ID': event.creativeId,
          'Is empty?': event.isEmpty,
          'Line Item ID': event.lineItemId,
          Size: event.size,
          'Source Agnostic Creative ID': event.sourceAgnosticCreativeId,
          'Source Agnostic Line Item ID': event.sourceAgnosticLineItemId,
        })
      })

      window.googletag.pubads().enableSingleRequest()
      window.googletag.pubads().disableInitialLoad()
      window.googletag.enableServices()

      for (const adSlotSetting of adSlotSettings) {
        const {
          htmlElements: { adContainer },
        } = adSlotSetting
        window.googletag.display(adContainer.id)
      }
    } catch (error) {
      log('Error while setting up ad banners', error)
    }
  })
}

// Refactor to use object as parameter
const editGameIframe = (width?: string, height?: string, marginTop?: string) => {
  const gameIFrame = document.querySelector(`#${GAME_TARGET_ID}`) as HTMLIFrameElement
  if (width) gameIFrame.style.width = width
  if (height) gameIFrame.style.height = height
  if (marginTop) gameIFrame.style.marginTop = marginTop
  return true
}

const pauseAd = (adBannerTypes: bannerTypeArray) => {
  log('pause banner ad', adBannerTypes)

  if (adBannerTypes.includes('horizontal') && bannersControl.horizontal.isShown) {
    hideAd(['horizontal'])
    editGameIframe(undefined, gameStyleHeight(), gameStyleMarginTop())
    clearTimeout(refreshHorizontalTimeoutRef)
    trackIga(actionTypes.BANNER_INACTIVE, 'horizontal')
    log('Horizontal ad banner paused')
  }

  if (adBannerTypes.includes('vertical') && bannersControl.vertical.isShown) {
    hideAd(['vertical'])
    editGameIframe(gameStyleWidth())
    clearTimeout(refreshVerticalTimeoutRef)
    trackIga(actionTypes.BANNER_INACTIVE, 'vertical')
    log('Vertical ad banner paused')
  }
}

const resumeAd = (adBannerTypes: bannerTypeArray) => {
  // Resize game iframe, display ad banner and refresh ads

  if (
    adBannerTypes.includes('horizontal') &&
    bannersControl.horizontal.isEnabled &&
    !bannersControl.horizontal.isShown
  ) {
    showAd(['horizontal'])
    editGameIframe(undefined, gameStyleHeight(), gameStyleMarginTop())
    refreshHorizontalSlot(globalRefreshTime, true)
    trackIga(actionTypes.BANNER_ACTIVE, 'horizontal')
    log('Horizontal ad banner resumed')
  }

  if (adBannerTypes.includes('vertical') && bannersControl.vertical.isEnabled && !bannersControl.vertical.isShown) {
    showAd(['vertical'])
    editGameIframe(gameStyleWidth())
    refreshVerticalSlot(globalRefreshTime, true)
    trackIga(actionTypes.BANNER_ACTIVE, 'vertical')
    log('Vertical ad banner resumed')
  }
}

const onPassiveTabHandler = () => {
  // Stop refreshing when on a passive tab
  if (bannersControl.isActiveTab) {
    log('Stopping ads refreshment on passive tab')
    bannersControl.isActiveTab = false
    clearTimeout(refreshHorizontalTimeoutRef)
    clearTimeout(refreshVerticalTimeoutRef)
  }
}

const onActiveTabHandler = () => {
  if (!bannersControl.isActiveTab) {
    // Start refreshing only on the first time after getting back to game tab
    log('Resuming ads refreshment on active tab')
    bannersControl.isActiveTab = true

    if (bannersControl.horizontal.isEnabled && bannersControl.horizontal.isShown) {
      log('Horizontal ad banner refreshing')
      refreshHorizontalSlot(globalRefreshTime)
    }

    if (bannersControl.vertical.isEnabled && bannersControl.vertical.isShown) {
      log('Vertical ad banner refreshing')
      refreshVerticalSlot(globalRefreshTime)
    }
  }
}

// Callback function to execute when mutations are observed. Game.style.width in our case
const widthObserverCallback: MutationCallback = function (mutationsList) {
  for (const mutation of mutationsList) {
    const elementStyle = (mutation.target as HTMLIFrameElement).style
    if (elementStyle.width !== gameStyleWidth()) {
      elementStyle.width = gameStyleWidth()
    }
  }
}

// Callback function to execute when mutations are observed. Game.style.width in our case
const heightObserverCallback: MutationCallback = function (mutationsList) {
  for (const mutation of mutationsList) {
    const elementStyle = (mutation.target as HTMLIFrameElement).style
    if (elementStyle.height !== gameStyleHeight()) {
      elementStyle.height = gameStyleHeight()
      elementStyle.marginTop = gameStyleMarginTop()
    }
  }
}

const onResizeHandler = () => {
  const { innerHeight: height, innerWidth: width } = window

  // IF W>=728 AND H>=700 THEN Horizontal Banner Area location shown
  if (width >= minWindowWidthHorizontalAd && height >= minGameHeight + adPlaceholderHeight) {
    resumeAd(['horizontal'])
  } else if (bannersControl.horizontal.isShown) {
    pauseAd(['horizontal'])
  }

  // IF W>=1100 AND H>=600 THEN Vertical Banner Area location shown
  if (width >= minGameWidth + adPlaceholderWidth && height >= minGameHeight) {
    resumeAd(['vertical'])
  } else if (bannersControl.vertical.isShown) {
    pauseAd(['vertical'])
  }
}

const throttledOnResizeHandler = throttle(onResizeHandler, 100)

const startObserversForGame = () => {
  // Select the node that will be observed for mutations
  const gameIFrame = document.querySelector(`#${GAME_TARGET_ID}`)!

  // Options for the observer (which mutations to observe)
  const config = {
    attributes: true,
    subtree: false,
    childList: false,
    attributeFilter: ['style'],
  }

  // Start observing the target node for configured mutations
  heightObserver.observe(gameIFrame, config)
  widthObserver.observe(gameIFrame, config)

  // trigger the re-align of the banners
  onResizeHandler()

  // Listening to resize changes
  window.addEventListener('resize', throttledOnResizeHandler)
}

export const addGoogleTagLogic = (
  { refreshTime, adSlotSettings }: BannersConfigWithDefault,
  adBannerTypes: bannerTypeArray
) => {
  // TODO: refactor to only initialize allowed banners
  try {
    log('Setting up banners')

    const { width, height, bannerWidth, bannerHeight } = getGameIframeSize(adSlotSettings)

    gameWidthWithAd = width
    gameHeightWithAd = height
    gameMarginTop = bannerHeight

    setGameIframeSize(width, height, bannerHeight)

    adPlaceholderWidth = bannerWidth
    adPlaceholderHeight = bannerHeight

    for (const adSlotSetting of adSlotSettings) {
      // Creating blank space in the screen for placing the ads
      addBannerDiv(adSlotSetting)
    }

    minGameWidth = getGameMinWidth()
    minGameHeight = getGameMinHeight()

    widthObserver = new MutationObserver(widthObserverCallback)
    heightObserver = new MutationObserver(heightObserverCallback)

    globalRefreshTime = refreshTime

    initGPTLogic(refreshTime, adSlotSettings)

    bannersControl.horizontal.isShown = true
    bannersControl.vertical.isShown = true
    bannersControl.horizontal.isEnabled = true
    bannersControl.vertical.isEnabled = true

    startObserversForGame()

    if (!adBannerTypes.includes('vertical')) {
      bannersControl.vertical.isEnabled = false
      pauseAd(['vertical'])
    }

    if (!adBannerTypes.includes('horizontal')) {
      bannersControl.horizontal.isEnabled = false
      pauseAd(['horizontal'])
    }

    if (bannersControl.horizontal.isShown) {
      trackIga(actionTypes.BANNER_ACTIVE, 'horizontal')
    }

    if (bannersControl.vertical.isShown) {
      trackIga(actionTypes.BANNER_ACTIVE, 'vertical')
    }
  } catch (error) {
    log('Failed to load ads', error)
  }
}

const initBannerAd = async (config: BannersConfigWithDefault, adBannerTypes: bannerTypeArray) => {
  if (window.googletag?.apiReady) {
    log('Google publisher tag was already initialized.')
    enableAd(adBannerTypes)
  } else {
    // We must load GPT logic before prebid
    await initGPTScript()
    addGoogleTagLogic(config, adBannerTypes)
    await startAdsAuction(adSlot, config)
  }
}

const initGPTScript = () => {
  log('Google publisher tag is loading...')
  return loadScript(GOOGLE_PUBLISHER_TAG)
}

const enableAd = (adBannerTypes: bannerTypeArray) => {
  log('Enable ad: ', adBannerTypes)

  bannersControl.vertical.isEnabled = adBannerTypes.includes('vertical')
  bannersControl.horizontal.isEnabled = adBannerTypes.includes('horizontal')

  startObserversForGame()

  if (!adBannerTypes.includes('vertical')) {
    pauseAd(['vertical'])
  }

  if (!adBannerTypes.includes('horizontal')) {
    pauseAd(['horizontal'])
  }
}

const disableAd = (adBannerTypes: bannerTypeArray) => {
  log('Disabling banner ads:', adBannerTypes)
  if (adBannerTypes.includes('vertical')) {
    widthObserver?.disconnect()
    bannersControl.vertical.isEnabled = false
  }

  if (adBannerTypes.includes('horizontal')) {
    heightObserver?.disconnect()
    bannersControl.horizontal.isEnabled = false
  }

  pauseAd(adBannerTypes)

  if (!bannersControl.horizontal.isShown && !bannersControl.vertical.isShown) {
    window.removeEventListener('resize', throttledOnResizeHandler)
  }
}

const getBannerType = (slotId: string) => {
  if (slotId.includes('horizontal')) {
    return 'horizontal'
  }

  if (slotId.includes('vertical')) {
    return 'vertical'
  }

  log(`Unknown slot type. ID: ${slotId}`)
  throw new Error(`Unknown slot type. ID: ${slotId}`)
}

export { initBannerAd, disableAd }
